import * as React from 'react';
import * as styles from '../report.module.scss';
import MaterialIcon from '@material/react-material-icon';
import PublicEye from '../../../images/icons/public-eye';
import PrivateEye from '../../../images/icons/private-eye';
import ConfirmationModal from './confirmationModal';
import TertiaryButton from '../../common/tertiary-button';
import { Button } from '@material-ui/core';
import { CustomView } from './useCustomViews';

interface Props {
  // The base report viz object
  viz: any;
  // The set of all saved custom views, for this user or others' public views
  customViews: CustomView[];
  setCustomViews: React.Dispatch<React.SetStateAction<CustomView[]>>;
  // Whether the currently passed view is a public view from another user
  otherView: boolean;
  // Either the editable custom view state or, if null, the original view
  view?: CustomView;
}

/**
 * A single line in the manage views state of the custom views representing a view
 */
const EditingView = ({ viz, view, customViews, setCustomViews, otherView }: Props) => {
  const [isEditingName, setIsEditingName] = React.useState<boolean>(false);
  const [viewName, setViewName] = React.useState<string>('');
  const [showConfirmation, setShowConfirmation] = React.useState<boolean>(false);
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

  const updatePublicStatus = () => {
    view.raw.setAdvertised(!view.isPublic);
    view.raw.saveAsync().then(() => {
      setCustomViews(
        customViews.map((cur) => {
          if (cur.name === view.name) {
            cur.isPublic = !view.isPublic;
          }
          return cur;
        })
      );
    });
  };

  const updateViewName = () => {
    const newName = viewName;
    view.raw.setName(viewName);
    view.raw.saveAsync().then(() => {
      setCustomViews(
        customViews.map((cur) => {
          if (cur.name === view.name) {
            cur.name = newName;
          }
          return cur;
        })
      );
    });
    setIsEditingName(false);

    setViewName(view.name);
  };

  const updateDefault = () => {
    setShowConfirmation(false);
    if (!view) {
      // if this is the original view
      // Update the views state to show none as default
      const updatedViews = [...customViews].map((item) => {
        item.isDefault = false;
        return item;
      });
      setCustomViews(updatedViews);
      // Reset the view to the original (null custom view), and set as default.
      viz.workbook.showCustomViewAsync(null).then(() => {
        viz.workbook.setActiveCustomViewAsDefaultAsync();
      });
    } else {
      const updatedViews = [...customViews].map((item) => {
        if (view.name === item.name) {
          item.isDefault = true;
        } else {
          item.isDefault = false;
        }
        return item;
      });

      setCustomViews(updatedViews);

      viz.workbook.showCustomViewAsync(view.name).then(() => {
        viz.workbook.setActiveCustomViewAsDefaultAsync();
      });
    }
  };

  const closeModal = () => {
    if (deleteViews) {
      setIsDeleting(false);
    }
    setShowConfirmation(false);
  };

  const deleteViews = () => {
    view.raw.workbook.removeCustomViewAsync(view.name).then(() => {
      const newViews = customViews.filter((item) => view.name !== item.name);
      setCustomViews(newViews);
    });
    setShowConfirmation(false);
  };

  const renderChangeDefaultDialog = () => {
    return (
      <div className={styles.container}>
        <div>
          <div className={`${styles.confirmationHeader} ${styles.modalHeader}`}>
            <h1>Confirmation</h1>
          </div>
          <span className="body2">
            Changing the default view will automatically switch the view displaying on the screen to the selected view.
            Any unsaved changes may be lost. Would you like to continue?
          </span>
        </div>
        <div className={styles.buttonContainer}>
          <TertiaryButton text="No" title="No" handleClick={() => setShowConfirmation(false)} />
          <Button className={styles.confirmButton} onClick={() => updateDefault()}>
            Yes
          </Button>
        </div>
      </div>
    );
  };

  const renderDeleteViewDialog = () => {
    return (
      <div className={styles.container}>
        <div>
          <div className={`${styles.confirmationHeader} ${styles.modalHeader}`}>
            <h1>Confirmation</h1>
          </div>
          <span className="body2">The selected view will be deleted. Would you like to continue?</span>
        </div>
        <div className={styles.buttonContainer}>
          <TertiaryButton text="No" title="No" handleClick={() => setShowConfirmation(false)} />
          <Button className={styles.confirmButton} onClick={() => deleteViews()}>
            Yes
          </Button>
        </div>
      </div>
    );
  };

  /**
   * Render the 'Original' view line, showing the unmodified version of the viz
   */
  if (!view) {
    const defaultExists = !!customViews.find((v) => v.isDefault);
    return (
      <React.Fragment>
        <span className={styles.viewManage}>
          <span className={styles.manageOtherViewName}>
            {!defaultExists ? (
              <MaterialIcon title="Default View" icon="insert_chart" />
            ) : (
              <MaterialIcon
                title="Select as Default"
                onClick={() => setShowConfirmation(true)}
                icon="insert_chart_outlined"
              />
            )}
            <span>{`Original ${!defaultExists ? '(default)' : ''}`}</span>
          </span>
        </span>
        <ConfirmationModal
          open={showConfirmation}
          closeModal={() => closeModal()}
          children={isDeleting ? renderDeleteViewDialog() : renderChangeDefaultDialog()}
        />
      </React.Fragment>
    );
  }

  /**
   * Render the record for a public custom view made by another user
   */
  if (otherView) {
    return (
      <React.Fragment>
        <span
          className={`${isEditingName ? styles.editingContainer : ''} ${
            view.isDefault ? styles.viewManageDefault : styles.viewManage
          }`}
          key={view.url}
        >
          <span className={styles.manageOtherViewName}>
            {view.isDefault ? (
              <MaterialIcon title="Default View" icon="insert_chart" />
            ) : (
              <MaterialIcon
                title="Select as Default"
                onClick={() => setShowConfirmation(true)}
                icon="insert_chart_outlined"
              />
            )}
            <span>{`${view.name} ${view.name ? '(default)' : ''}`}</span>
          </span>
          <span className={styles.otherViewOwner}>{view.ownerName}</span>
        </span>
        <ConfirmationModal
          open={showConfirmation}
          closeModal={() => closeModal()}
          children={isDeleting ? renderDeleteViewDialog() : renderChangeDefaultDialog()}
        />
      </React.Fragment>
    );
  }

  /**
   * Render the view for the user's own, fully editable custom views
   */
  return (
    <span
      className={`${isEditingName ? styles.editingContainer : ''} ${
        view.isDefault ? styles.viewManageDefault : styles.viewManage
      }`}
      key={view.url}
    >
      {isEditingName ? (
        <React.Fragment>
          <input
            autoFocus
            onFocus={() => setViewName(view.name)}
            onChange={(event) => setViewName(event.target.value)}
            onBlur={updateViewName}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                updateViewName();
              }
            }}
            value={viewName}
          />
          <MaterialIcon title="Close" icon="close" className={styles.closeIcon} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <span className={styles.manageMyViewName}>
            {view.isDefault ? (
              <MaterialIcon title="Default View" icon="insert_chart" />
            ) : (
              <MaterialIcon
                title="Select as Default"
                onClick={() => setShowConfirmation(true)}
                icon="insert_chart_outlined"
              />
            )}
            <span>{`${view.name} ${view.isDefault ? '(default)' : ''}`}</span>
          </span>
          <span className={styles.manageMyViewActions}>
            <MaterialIcon title="Edit Name" icon="edit" onClick={() => setIsEditingName(true)} />
            <MaterialIcon
              title="Delete"
              icon="delete"
              onClick={() => {
                setIsDeleting(true);
                setShowConfirmation(true);
              }}
            />
            {view.isPublic ? (
              <PublicEye width="16" height="16" handleClick={() => updatePublicStatus()} />
            ) : (
              <PrivateEye width="16" height="16" handleClick={() => updatePublicStatus()} />
            )}
          </span>
        </React.Fragment>
      )}
      <ConfirmationModal
        open={showConfirmation}
        closeModal={() => closeModal()}
        children={isDeleting ? renderDeleteViewDialog() : renderChangeDefaultDialog()}
      />
    </span>
  );
};

export default EditingView;
